import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {monthRoundedTime} from '@wandb/weave/common/util/time';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {IconName} from '@wandb/weave/components/Icon';
import {RunState} from '@wandb/weave/components/RunState';
import {Pill} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Timestamp} from '@wandb/weave/components/Timestamp';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {CellInfo, Column, SortingRule} from 'react-table';
import {toast} from 'react-toastify';
import {DataRow} from 'src/components/WBReactTable';

import {
  RunQueueItemInfoDetails,
  TemplateVariable,
  useDeleteFromRunQueueMutation,
} from '../../../generated/graphql';
import {
  useIsAdminModeActiveAndIsNotLocalEnv,
  viewingAs,
} from '../../../util/admin';
import {agent as agentUrl, job as jobUrl} from '../../../util/urls';
import {LinkProject} from '../LinkProject';
import {LinkRun} from '../LinkRun';
import {Priority} from '../Priority';
import {Table} from '../Table';
import {RunInfo, RunQueueItem} from '../types';
import {User} from '../User';
import {padLeft, WANDB_ENTITY_NAME, WANDB_PUBLIC_QUEUE_NAME} from '../utils';
import * as S from './QueueItemsTable.styles';
import {RunQueueItemInfoDrawer} from './RunQueueItemInfoDrawer/RunQueueItemInfoDrawer';
import {
  RunQueueItemIssueDrawer,
  RunQueueItemIssueDrawerProps,
} from './RunQueueItemIssueDrawer';

type QueueItemsTableProps = {
  entityName: string;
  items: RunQueueItem[];
  border: string;
  queueId: string;
  queueName: string;
  templateVariables: TemplateVariable[];
  showPriority?: boolean;
  refetchQueue: () => void;
};

const getRunDuration = (run: RunInfo) => {
  const heartbeat =
    run.heartbeatAt != null ? new Date(run.heartbeatAt) : new Date(); // default to now
  const created = new Date(run.createdAt);
  return (heartbeat.getTime() - created.getTime()) / 1000;
};

type SelectedRunQueueItemInfo = Pick<
  RunQueueItemIssueDrawerProps,
  | 'runName'
  | 'projectName'
  | 'displayState'
  | 'errorInfo'
  | 'warningInfos'
  | 'agentRun'
  | 'agentId'
  | 'associatedRunId'
>;

const ACTIVE_STATES: readonly string[] = [
  'running',
  'stopping',
  'preempting',
  'starting',
  'idle',
  'pending',
] as const;

export const QueueItemsTable = ({
  entityName,
  items,
  border,
  queueId,
  queueName,
  templateVariables,
  refetchQueue,
  showPriority,
}: QueueItemsTableProps) => {
  const location = useLocation();
  const history = useHistory();
  const isAdminMode =
    useIsAdminModeActiveAndIsNotLocalEnv() || viewingAs() !== '';
  const [viewedItemIssues, setViewedItemIssues] =
    useState<SelectedRunQueueItemInfo | null>(null);

  const [viewedItemIndex, setViewedItemIndex] = useState<number | null>(null);
  const [viewedItemInfo, setViewedItemInfo] = useState<RunQueueItem | null>(
    null
  );

  const changeViewedItemInfo = useCallback(
    (item: RunQueueItem, index: number) => {
      setViewedItemIndex(index);
      setViewedItemInfo(item);

      // Add itemId to URL when opening drawer
      const urlParams = new URLSearchParams(location.search);
      const params: string[] = [];

      // Preserve existing params except itemId
      for (const [key, value] of urlParams.entries()) {
        if (key !== 'itemId') {
          params.push(`${key}=${value}`);
        }
      }

      params.push(`itemId=${item.id}`);

      history.replace({
        pathname: location.pathname,
        search: params.length > 0 ? params.join('&') : '',
      });
    },
    [location.pathname, location.search, history]
  );

  const closeDrawer = useCallback(() => {
    setViewedItemInfo(null);

    // remove item id from url
    const urlParams = new URLSearchParams(location.search);
    const params: string[] = [];

    for (const [key, value] of urlParams.entries()) {
      if (key !== 'itemId') {
        params.push(`${key}=${value}`);
      }
    }

    history.replace({
      pathname: location.pathname,
      search: params.length > 0 ? params.join('&') : '',
    });
  }, [location.pathname, location.search, history]);

  useEffect(() => {
    // check for item id in url to open drawer automatically
    const urlParams = new URLSearchParams(location.search);
    const itemId = urlParams.get('itemId');

    if (itemId && items.length > 0 && !viewedItemInfo) {
      const itemIndex = items.findIndex(item => item.id === itemId);
      if (itemIndex !== -1) {
        changeViewedItemInfo(items[itemIndex], itemIndex);
      }
    }
  }, [location.search, items, viewedItemInfo, changeViewedItemInfo]);

  const [deletingRunQueueItem, setDeletingRunQueueItem] =
    useState<DeleteRunQueueInfo | null>(null);

  const rows = useMemo(
    () =>
      items.map(item => {
        const clonedItem = JSON.parse(JSON.stringify(item)); // deep copy
        const {
          versionedJob,
          projectName,
          displayState,
          run,
          associatedRunId,
          error,
          warnings,
        } = clonedItem;
        const errorWarnings = {
          error,
          warnings,
        };
        const runName =
          run && run.displayName ? run.displayName : associatedRunId;
        if (run && run.displayName && item.runSpec._resume_count) {
          run.displayName += ' (resumed)';
        }

        const rqiDeleteInfo =
          item.state === 'PENDING'
            ? {
                queueId,
                queueItemId: item.id,
                jobName: versionedJob,
              }
            : null;

        let sortScore = '';
        const state = clonedItem.displayState.toLowerCase();
        if (ACTIVE_STATES.includes(state)) {
          // Running and other active states go on top, with longest-running items first
          // (and separated by priority if necessary)
          sortScore += '0-';
          if (showPriority) {
            sortScore += padLeft(String(item.priority), 10, '0') + '-';
          }
          sortScore += new Date(item.updatedAt ?? item.createdAt).getTime();
        } else if (state === 'queued') {
          // Queued items go second, with longest-queued items first
          // (and separated by priority if necessary)
          sortScore += '1-';
          if (showPriority) {
            sortScore += padLeft(String(item.priority), 10, '0') + '-';
          }
          sortScore += new Date(item.createdAt).getTime();
        } else {
          // All other states are terminal and go to the bottom of the queue,
          // with recently completed items first (ignoring priority)
          sortScore += '2-';
          const updated = new Date(item.updatedAt ?? item.createdAt).getTime();
          sortScore += String(10 ** 13 - updated);
        }

        return {
          searchString: `${versionedJob}|${projectName}|${runName}|${displayState}`,
          row: {
            ...item.templateVariableValues,
            ...clonedItem,
            startTime: run ? run.createdAt : null,
            duration: run ? getRunDuration(run) : null,
            runName,
            errorWarnings,
            rqiDeleteInfo,
            sortScore,
            clonedItem,
          },
        };
      }),
    [items, queueId, showPriority]
  );

  const columns: Column[] = [
    {
      Header: 'Run',
      accessor: 'runName',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        if (cellInfo.original.run) {
          const {name, displayName} = cellInfo.original.run;
          return (
            <LinkRun
              entityName={cellInfo.original.entityName}
              projectName={cellInfo.original.projectName}
              name={name}
              displayName={displayName}
            />
          );
        }
        // If we have a associatedRunId but no run, some possibilities:
        // 1. The item was claimed by the agent and the associated_run_id is filled but
        //    then the job crashed without a call to wandb.init
        // 2. The item hasn't crashed, but is still starting.
        return <div>{cellInfo.value}</div>;
      },
    },
    {
      Header: 'Job',
      accessor: 'versionedJob',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        // If the run spec includes a job ID we can link to
        // the job details for that version.
        const {jobEntity, jobProject, runSpec} = cellInfo.original;
        const itemJobId = runSpec?._wandb_job_collection_id;
        if (itemJobId) {
          const urlParams = {
            entityName: jobEntity,
            projectName: jobProject,
            jobId: itemJobId,
            jobVersion: cellInfo.original.alias,
            tab: 'version_details',
          };
          const url = jobUrl(urlParams);
          return (
            <div className="truncate">
              <Link
                to={{
                  pathname: url,
                  state: {
                    from: {
                      url: window.location.pathname,
                      text: 'Launch',
                    },
                  },
                }}>
                {cellInfo.value}
              </Link>
            </div>
          );
        }
        return <div>{cellInfo.value}</div>;
      },
    },
    {
      Header: 'Project',
      accessor: 'projectName',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <LinkProject
            entityName={cellInfo.original.entityName}
            projectName={cellInfo.value}
          />
        );
      },
    },
    {
      Header: 'Status',
      accessor: 'displayState',
      Cell: (cellInfo: CellInfo) => {
        return <RunState value={cellInfo.value} tooltip />;
      },
    },
    {
      Header: 'Errors',
      accessor: 'errorWarnings',
      minWidth: 150,
      maxWidth: 150,
      Cell: (cellInfo: CellInfo) => {
        const {error, warnings} = cellInfo.value;
        let count = 0;
        const stages: string[] = [];
        if (error != null && error.message !== '') {
          count += 1;
          stages.push(error.stage);
        }
        if (warnings != null) {
          count += warnings.length;
          warnings.forEach((warning: RunQueueItemInfoDetails) => {
            stages.push(warning.stage);
          });
        }
        if (count === 0) {
          return <></>;
        }
        let label = count.toString() + ' ';
        if (stages.length === 1) {
          label += stages[0] + ' issue';
          if (count > 1) {
            label += 's';
          }
        } else {
          label += 'various issues';
        }
        const iconName = error != null ? 'failed' : 'warning';
        const pillColor = error != null ? 'red' : 'gold';

        return (
          <S.InfoPillContainer
            onClick={() =>
              setViewedItemIssues({
                runName:
                  cellInfo.original.run?.displayName ??
                  cellInfo.original.run?.name,
                projectName: cellInfo.original.projectName,
                displayState: cellInfo.original.displayState,
                errorInfo: error,
                warningInfos: warnings,
                agentRun: cellInfo.original.launchAgentName,
                agentId: cellInfo.original.launchAgentId,
                associatedRunId: cellInfo.original.associatedRunId,
              })
            }>
            <Pill icon={iconName as IconName} label={label} color={pillColor} />
          </S.InfoPillContainer>
        );
      },
    },
    {
      accessor: 'sortScore',
      show: false,
    },
    {
      Header: 'Author',
      accessor: 'author',
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value ? <User username={cellInfo.value} /> : 'Unknown';
      },
    },
    {
      Header: 'Agent',
      accessor: 'launchAgentName',
      Cell: (cellInfo: CellInfo) => {
        const {launchAgentId} = cellInfo.original;
        if (!launchAgentId) {
          return null;
        }
        const url = agentUrl(cellInfo.original.entityName, launchAgentId);
        return <Link to={url}>{cellInfo.value}</Link>;
      },
    },
    {
      Header: 'Submitted date',
      accessor: 'createdAt',
      Cell: (cellInfo: CellInfo) => {
        return <Timestamp value={cellInfo.value} />;
      },
    },
    {
      Header: 'Start time',
      accessor: 'startTime',
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value ? <Timestamp value={cellInfo.value} /> : null;
      },
    },
    {
      Header: 'Duration',
      accessor: 'duration',
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value != null ? monthRoundedTime(cellInfo.value) : null;
      },
    },
    {
      Header: '',
      accessor: 'rqiDeleteInfo',
      className: 'delete',
      resizable: true,
      minWidth: 50,
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value ? (
          <Button
            variant="ghost"
            icon="delete"
            tooltip="This item will be canceled."
            onClick={(e: React.SyntheticEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setDeletingRunQueueItem(cellInfo.value);
            }}
          />
        ) : (
          <></>
        );
      },
    },
  ];

  templateVariables.forEach(tv => {
    columns.push({
      Header: tv.name,
      id: tv.name,
      accessor: row => {
        if (!row.templateVariableValues) {
          return null;
        }
        return row.templateVariableValues[tv.name];
      },
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value ? (
          <div data-test={tv.name}>{cellInfo.value}</div>
        ) : (
          <></>
        );
      },
    });
  });

  const defaultSortedRows = useMemo(() => {
    return rows.sort((a, b) => {
      return a.row.sortScore.localeCompare(b.row.sortScore);
    });
  }, [rows]);

  const [sortedRows, setSortedRows] = useState<DataRow[]>(defaultSortedRows);

  const nextViewedItem = useCallback(() => {
    if (viewedItemIndex !== null) {
      const nextIndex = viewedItemIndex + 1;
      if (nextIndex < sortedRows.length) {
        setViewedItemIndex(nextIndex);
        setViewedItemInfo(sortedRows[nextIndex].row.clonedItem);
      }
    }
  }, [viewedItemIndex, sortedRows]);

  const previousViewedItem = useCallback(() => {
    if (viewedItemIndex !== null) {
      const nextIndex = viewedItemIndex - 1;
      if (nextIndex >= 0) {
        setViewedItemIndex(nextIndex);
        setViewedItemInfo(sortedRows[nextIndex].row.clonedItem);
      }
    }
  }, [viewedItemIndex, sortedRows]);

  const onSortedChange = useCallback(
    (newSorted: SortingRule[]) => {
      const {id, desc} = newSorted[0];
      const newSortedRows = [...defaultSortedRows].sort(
        (a: DataRow, b: DataRow) => {
          const multiplier = desc ? 1 : -1;
          if (
            a.row[id] != null &&
            b.row[id] != null &&
            b.row[id].toString().localeCompare(a.row[id].toString()) !== 0
          ) {
            return (
              b.row[id].toString().localeCompare(a.row[id].toString()) *
              multiplier
            );
          }
          if (a.row[id] == null) {
            return multiplier;
          }
          if (b.row[id] == null) {
            return -1 * multiplier;
          }
          return -1 * multiplier;
        }
      );
      setSortedRows(newSortedRows);
    },
    [defaultSortedRows]
  );

  const isPublicQueue =
    queueName === WANDB_PUBLIC_QUEUE_NAME && entityName === WANDB_ENTITY_NAME;

  if (!isPublicQueue || isAdminMode) {
    columns.push({
      Header: '',
      accessor: 'details',
      className: 'details',
      minWidth: 80,
      Cell: (cellInfo: CellInfo) => {
        return (
          <div className="details-button-container m-0 mb-2 mr-4 flex h-full items-center justify-center p-1 px-4 py-1 pl-2">
            <Button
              variant="ghost"
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault();
                e.stopPropagation();
                const viewedIndex = sortedRows.findIndex(
                  r => r.row.id === cellInfo.original.id
                );
                changeViewedItemInfo(cellInfo.original, viewedIndex);
              }}>
              Details
            </Button>
          </div>
        );
      },
    });
  }

  // If queue has prioritization enabled, add column for priority
  if (showPriority) {
    // Insert priority column after 'Status'
    columns.splice(
      columns.findIndex(c => c.Header === 'Status'),
      0,
      {
        Header: 'Priority',
        accessor: 'priority',
        Cell: (cellInfo: CellInfo) => {
          return <Priority value={cellInfo.value} />;
        },
      }
    );
  }

  const defaultSorted = [{id: 'sortScore', desc: false}];

  return (
    <>
      {viewedItemInfo && (
        <RunQueueItemInfoDrawer
          open={viewedItemInfo !== null}
          index={viewedItemIndex ?? 0}
          maxIndex={items.length - 1}
          runQueueItem={viewedItemInfo}
          nextViewedItem={nextViewedItem}
          previousViewedItem={previousViewedItem}
          onClose={closeDrawer}
        />
      )}
      {viewedItemIssues && (
        <RunQueueItemIssueDrawer
          open={viewedItemIssues !== null}
          entityName={entityName}
          projectName={viewedItemIssues.projectName}
          agentRun={viewedItemIssues.agentRun}
          agentId={viewedItemIssues.agentId}
          associatedRunId={viewedItemIssues.associatedRunId}
          onClose={() => setViewedItemIssues(null)}
          runName={viewedItemIssues.runName}
          displayState={viewedItemIssues.displayState}
          errorInfo={viewedItemIssues.errorInfo}
          warningInfos={viewedItemIssues.warningInfos}
        />
      )}
      {deletingRunQueueItem != null && (
        <DeleteRunQueueItemModal
          open
          deleteRunQueueInfo={deletingRunQueueItem}
          onClose={() => {
            setDeletingRunQueueItem(null);
          }}
          onDelete={async () => {
            window.analytics?.track('Deleted run queue item', {
              sourceEntity: entityName,
              deletingRunQueueItem,
            });
            await refetchQueue();
            setDeletingRunQueueItem(null);
          }}
        />
      )}
      <Tailwind>
        <Table
          border={border}
          data={defaultSortedRows}
          columns={columns}
          pageSize={20}
          onSortedChange={onSortedChange}
          defaultSorted={defaultSorted}
        />
      </Tailwind>
    </>
  );
};

interface DeleteRunQueueInfo {
  queueId: string;
  queueItemId: string;
  jobName?: string;
}

interface DeleteRunQueueItemModalProps {
  open: boolean;
  deleteRunQueueInfo: DeleteRunQueueInfo;
  onClose(): void;
  onDelete(): void;
}

const DeleteRunQueueItemModal: React.FC<DeleteRunQueueItemModalProps> = ({
  open,
  deleteRunQueueInfo,
  onClose,
  onDelete,
}) => {
  const [deleteFromRunQueue] = useDeleteFromRunQueueMutation();
  const [isInFlight, setIsInFlight] = useState(false);

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{style: {borderRadius: 10}}}>
      <div style={{margin: '36px 16px 24px 16px'}}>
        <DialogTitle>
          <S.ModalTitle>Delete this run?</S.ModalTitle>
        </DialogTitle>
        <DialogContent>
          <S.ModalContent>
            This scheduled run{' '}
            {deleteRunQueueInfo.jobName ? (
              <>
                created from{' '}
                <b style={{fontWeight: 600}}>{deleteRunQueueInfo.jobName}</b>
              </>
            ) : (
              ''
            )}{' '}
            will be stopped and removed.
          </S.ModalContent>
        </DialogContent>
        <DialogActions>
          <Button
            variant="destructive"
            size="large"
            className="ml-12"
            disabled={isInFlight}
            onClick={async e => {
              setIsInFlight(true);
              e.preventDefault();
              e.stopPropagation();
              try {
                await deleteFromRunQueue({
                  variables: {
                    queueID: deleteRunQueueInfo.queueId,
                    runQueueItemId: deleteRunQueueInfo.queueItemId,
                  },
                });
                onDelete();
              } catch (error) {
                toast.error(`Error deleting enqueued item: ${error}`);
              }
              setIsInFlight(false);
            }}>
            Delete run
          </Button>
          <Button
            variant="ghost"
            size="large"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}>
            Cancel
          </Button>
          <div style={{flexGrow: 1}} />
        </DialogActions>
      </div>
    </Dialog>
  );
};
